import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import NotFound from '@/pages/NotFound';
import { Spinner } from '@/components/ui/EmptyState';
import { Seo } from '@/components/Seo';
import { supabase } from '@/lib/supabaseClient';
import type { Article, BodyBlock } from '@/types/article';
import { useSiteDesign } from '@/hooks/useSiteDesign';
import ArticleV2 from '../v2/ArticleV2';

const SITE_URL = 'https://rxk.thekseoul.com';

function parseInlineBold(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-[#2A2A2A]">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function renderBodyBlock(block: BodyBlock, index: number): React.ReactNode {
  switch (block.type) {
    case 'paragraph':
      return (
        <p
          key={index}
          className="text-sm text-[#5A5A5A] font-light leading-[1.9] mb-5"
        >
          {parseInlineBold(block.content)}
        </p>
      );
    case 'heading':
      return (
        <h2
          key={index}
          className="font-serif text-xl md:text-2xl font-bold text-[#2A2A2A] mt-10 mb-4"
        >
          {block.content}
        </h2>
      );
    case 'subheading':
      return (
        <h3
          key={index}
          className="text-sm font-semibold text-[#2A2A2A] mt-6 mb-3 tracking-wide"
        >
          {block.content}
        </h3>
      );
    case 'list':
      return (
        <ul key={index} className="flex flex-col gap-2.5 mb-6 pl-1">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-sm text-[#5A5A5A] font-light leading-[1.8]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#7B1113] mt-2 shrink-0" />
              {parseInlineBold(item)}
            </li>
          ))}
        </ul>
      );
    case 'blockquote':
      return (
        <blockquote
          key={index}
          className="my-8 pl-5 border-l-2 border-[#7B1113] py-2"
        >
          <p className="text-sm text-[#2A2A2A] font-light italic leading-[1.9]">
            {block.content}
          </p>
        </blockquote>
      );
    default:
      return null;
  }
}

export default function ArticleDetailPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null | undefined>(undefined);
  const { isNew } = useSiteDesign();

  useEffect(() => {
    if (!slug) return;
    let mounted = true;
    supabase
      .from('rxk_articles')
      .select('id, slug, category, category_color, read_time, title, excerpt, author, author_role, published_date, image, body, status')
      .eq('slug', slug)
      .eq('status', 'published')
      .maybeSingle()
      .then(({ data }) => {
        if (mounted) setArticle((data as Article) ?? null);
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (article === undefined) {
    return (
      <main className="w-full min-h-screen bg-white font-sans">
        <SharedNavbar />
        <div className="pt-32 flex justify-center">
          <Spinner />
        </div>
        <SharedFooter />
      </main>
    );
  }

  if (!article) return <NotFound />;

  const canonicalUrl = `${SITE_URL}/content/${article.slug}`;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image ? [article.image] : undefined,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.published_date,
    mainEntityOfPage: canonicalUrl,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Skincare Intelligence', item: `${SITE_URL}/content` },
      { '@type': 'ListItem', position: 3, name: article.title, item: canonicalUrl },
    ],
  };

  const seo = (
    <Seo
      title={`${article.title} | Rx.K Seoul`}
      description={article.excerpt ?? undefined}
      image={article.image || undefined}
      jsonLd={[articleSchema, breadcrumbSchema]}
    />
  );
  if (isNew)
    return (
      <>
        {seo}
        <ArticleV2 article={article} />
      </>
    );

  return (
    <main className="w-full min-h-screen bg-white font-sans">
      <Seo
        title={`${article.title} | Rx.K Seoul`}
        description={article.excerpt}
        image={article.image || undefined}
        jsonLd={[articleSchema, breadcrumbSchema]}
      />
      <SharedNavbar />

      <article className="w-full pt-24 md:pt-28 pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link
            to="/content"
            className="inline-flex items-center gap-1.5 text-xs text-[#8A8A8A] hover:text-[#7B1113] transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line" /> {t('content.allArticles')}
          </Link>

          {/* Meta */}
          <div className="flex items-center gap-3 mb-5">
            <span
              className="text-[10px] tracking-[0.1em] uppercase px-2.5 py-1 rounded-full text-white font-medium"
              style={{ backgroundColor: article.category_color }}
            >
              {article.category}
            </span>
            <span className="text-[11px] text-[#8A8A8A] font-light">
              {article.read_time}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#2A2A2A] leading-[1.15] mb-5">
            {article.title}
          </h1>

          {/* Excerpt */}
          <p className="text-base md:text-lg text-[#8A8A8A] font-light leading-[1.7] mb-8">
            {article.excerpt}
          </p>

          {/* Author + date */}
          <div className="flex items-center gap-3 pb-8 mb-8 border-b border-[#E8E8E6]">
            <div className="w-9 h-9 rounded-full bg-[#F0EFED] flex items-center justify-center">
              <i className="ri-user-line text-[#8A8A8A] text-sm" />
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-medium text-[#2A2A2A]">
                {article.author}
              </span>
              <span className="text-[11px] text-[#8A8A8A] font-light">
                {article.author_role && `${article.author_role} · `}
                {article.published_date}
              </span>
            </div>
          </div>

          {/* Hero image */}
          {article.image && (
            <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Body */}
          <div className="flex flex-col">
            {article.body.map((block, i) => renderBodyBlock(block, i))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-14 p-8 md:p-10 bg-[#F0EFED] rounded-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex flex-col gap-2">
              <h3 className="font-serif text-xl font-bold text-[#2A2A2A]">
                Ready to start your skincare journey?
              </h3>
              <p className="text-sm text-[#8A8A8A] font-light">
                Discover the 3-Step Daily Treatment designed for Bangladesh&apos;s
                climate.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/3-step-treatment"
                className="px-6 py-3 bg-[#7B1113] text-white text-xs tracking-[0.12em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center w-full sm:w-auto"
              >
                3-Step Treatment &rarr;
              </Link>
              <Link
                to="/products"
                className="px-6 py-3 border border-[#2A2A2A]/20 text-[#2A2A2A] text-xs tracking-[0.12em] uppercase rounded-full hover:bg-[#2A2A2A] hover:text-white transition-all cursor-pointer text-center w-full sm:w-auto"
              >
                Our Products &rarr;
              </Link>
            </div>
          </div>
        </div>
      </article>

      <SharedFooter />
    </main>
  );
}
